// App-wide company roster filter: All / Potential / Onboarded.
//
// One control in the header drives every view that lists companies (pipeline,
// portfolio, scoreboard, match, onboarding, standup, legal, proposals, capital,
// documents, reports, dashboards). Views call `inRoster(companyId)` on their
// rows; rows with no company (an unlinked proposal, an opportunity card) stay
// visible under "All" and drop out when a roster is picked, except where the
// view says otherwise. The choice is remembered per browser.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { CompanySegment } from "@arcline/shared";
import { api } from "./api";

export type RosterFilter = "all" | CompanySegment;
const KEY = "arcline_roster_filter";

interface RosterCtx {
  roster: RosterFilter;
  setRoster: (r: RosterFilter) => void;
  // id → segment for every company, refreshed after roster changes elsewhere.
  segments: Map<number, CompanySegment>;
  loaded: boolean;
  inRoster: (companyId: number | null | undefined, opts?: { keepUnlinked?: boolean }) => boolean;
  refresh: () => void;
  counts: { potential: number; onboarded: number };
}

const Ctx = createContext<RosterCtx | null>(null);

export function RosterProvider({ children }: { children: ReactNode }) {
  const [roster, setRosterState] = useState<RosterFilter>(() => {
    try { const v = localStorage.getItem(KEY); return v === "potential" || v === "onboarded" ? v : "all"; } catch { return "all"; }
  });
  const [segments, setSegments] = useState<Map<number, CompanySegment>>(new Map());
  const [loaded, setLoaded] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    api.companies().then((cos) => {
      if (!alive) return;
      setSegments(new Map(cos.map((co) => [co.id, co.segment])));
      setLoaded(true);
    }).catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [tick]);
  const setRoster = useCallback((r: RosterFilter) => {
    setRosterState(r);
    try { localStorage.setItem(KEY, r); } catch { /* storage blocked — works for this session */ }
  }, []);
  const refresh = useCallback(() => setTick((n) => n + 1), []);
  const value = useMemo<RosterCtx>(() => {
    let potential = 0, onboarded = 0;
    for (const s of segments.values()) { if (s === "onboarded") onboarded++; else potential++; }
    return {
      roster, setRoster, segments, loaded, refresh, counts: { potential, onboarded },
      inRoster: (companyId, opts) => {
        if (roster === "all") return true;
        if (companyId == null) return Boolean(opts?.keepUnlinked);
        // Until the map loads (or for a company created since), don't hide it.
        const seg = segments.get(companyId);
        return seg == null ? !loaded : seg === roster;
      },
    };
  }, [roster, setRoster, segments, loaded, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRoster(): RosterCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useRoster must be used inside RosterProvider");
  return v;
}

export const ROSTER_LABEL: Record<RosterFilter, string> = { all: "All companies", potential: "Potential", onboarded: "Onboarded" };
